<template>
  <div class="flex justify-between">
    <div
      v-if="anyOverflowing"
      class="pointer-events-none relative z-10 flex border-b border-gray-300/75"
    >
      <div class="flex items-center bg-white">
        <button
          class="pointer-events-auto rounded p-1 transition"
          :disabled="arrivedState.left"
          :class="[arrivedState.left ? 'opacity-50' : 'hover:bg-gray-300']"
          :title="$t('legal_text.middleColumn.navigation.scroll_tabs_to_left')"
          @click="scroll('left')"
        >
          <i-heroicons-chevron-left-20-solid class="h-4 w-4" />
        </button>
      </div>
      <div
        v-if="!arrivedState.left"
        class="absolute left-6 h-full w-8 bg-gradient-to-r from-white to-white/0"
      ></div>
    </div>
    <div
      ref="tabList"
      class="scrollbar-hidden flex cursor-grab items-stretch justify-start overflow-auto"
      @drop="onDrop"
      @dragover="dragOver"
      @dragenter.prevent
      @keydown="preventScrollEvent"
    >
      <div
        v-for="(tab, index) in tabs"
        :key="index"
        :ref="tabRefs.set"
        class="w-[7.7rem] flex-shrink-0 rounded-t-md border border-transparent border-b-gray-300/75 2xl:w-32"
        :class="{
          'border-x-gray-300/75 border-b-transparent border-t-gray-300/75':
            tab.active,
          'hover:bg-gray-200': !tab.active,
          'opacity-0': state.from === index,
          'translate-x-full':
            elementsWithTransform.includes(index) && elementsMoved < 0,
          '-translate-x-full':
            elementsWithTransform.includes(index) && elementsMoved > 0,
          'transition-[opacity,_background-color]': !dragging,
          transition: dragging,
        }"
        draggable="true"
        @dragstart="dragStart($event, index, tabRefs[index])"
      >
        <ParagraphTab
          class="h-full"
          :class="{
            'border-l border-gray-200':
              index > 0 &&
              index !== activeTabIndex &&
              index !== activeTabIndex + 1,
          }"
          v-bind="tab"
          :removable="removable"
          @click="switchActiveTab(index)"
          @delete="removeTab(index)"
        />
      </div>
    </div>
    <div
      v-if="anyOverflowing"
      class="pointer-events-none relative flex border-b border-gray-300/75"
    >
      <div
        v-if="!arrivedState.right"
        class="absolute right-6 h-full w-8 bg-gradient-to-l from-white to-white/0"
      ></div>
      <div class="flex items-center bg-white">
        <button
          class="pointer-events-auto rounded p-1 transition"
          :disabled="arrivedState.right"
          :class="[arrivedState.right ? 'opacity-50' : 'hover:bg-gray-300']"
          :title="$t('legal_text.middleColumn.navigation.scroll_tabs_to_right')"
          @click="scroll('right')"
        >
          <i-heroicons-chevron-right-20-solid class="h-4 w-4" />
        </button>
      </div>
    </div>
    <AddTabButton @click="handleAddTab" />
    <div
      class="flex grow items-center border-b border-gray-300/75 py-0.5"
    ></div>
  </div>
</template>

<script lang="ts" setup>
import type { ILegalText } from "~/models/ILegalText";
import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";

type Props = {
  legalText: ILegalText | undefined;
  legalTextParagraph: ILegalTextParagraph | undefined;
  loading: boolean;
};

const props = defineProps<Props>();

const { legalText, legalTextParagraph, loading } = toRefs(props);

const { data: tabs } = useFetchTabs();
const { activeTab, activeTabIndex } = useActiveTab();
const { addTab, changeActiveTab, switchActiveTab, removeTab, moveTab, toTab } =
  useTabsMutations();

const tabRefs = useTemplateRefsList<HTMLElement>();
const tabList = ref<HTMLElement>();

const listEl = computed(() => unrefElement(tabList));

const { arrivedState } = useScroll(tabList);

const anyOverflowing = ref(false);

watchImmediate(tabs, async () => {
  await nextTick();
  anyOverflowing.value = listEl.value
    ? listEl.value.scrollWidth > listEl.value.clientWidth
    : false;
});

const removable = computed(() => tabs.value.length > 1);
const { focusSearchBar } = useSearchbarFocus();

const handleAddTab = async () => {
  if (!legalText.value) {
    return;
  }
  await addTab(toTab(legalText.value, undefined));
  focusSearchBar();
};

const mounted = useMounted();

watchImmediate([legalText, legalTextParagraph, loading, mounted], async () => {
  if (!legalText.value || !mounted.value || loading.value) {
    return;
  }

  const currentRouteTab = toTab(legalText.value, legalTextParagraph.value);

  if (activeTab.value) {
    changeActiveTab(currentRouteTab);
  } else {
    await addTab(currentRouteTab);
  }
});

const isOverflowing = (element: HTMLElement, dir: "left" | "right") => {
  const list = listEl.value;
  if (!list) {
    return false;
  }
  const listPos = list.getBoundingClientRect();
  const elPos = element.getBoundingClientRect();
  if (dir === "left") return elPos.left <= listPos.left;
  else if (dir === "right") return elPos.right >= listPos.right;
};

const scroll = async (dir: "left" | "right") => {
  const elements: HTMLElement[] = [
    ...tabRefs.value.map((tab) => unrefElement(tab) as HTMLElement),
  ];
  let target: HTMLElement | undefined;
  if (dir === "right") {
    // find first element that is overflowing and make it the leftmost after scrolling
    target = elements.find((el) => isOverflowing(el, dir));
  } else {
    const index = elements.findIndex((el) => !isOverflowing(el, dir));
    target =
      elements[index - 3] ??
      elements[index - 2] ??
      elements[index - 1] ??
      elements[index];
  }
  if (target) {
    scrollToTab(target);
  }
};

const scrollToTab = (tab: HTMLElement) => {
  const list = listEl.value;
  if (!list) {
    return;
  }
  const { left: elLeft } = tab.getBoundingClientRect();
  const { left: listLeft } = list.getBoundingClientRect();
  const delta = elLeft - listLeft;
  // optional access needed for JSDOM
  list.scroll?.({
    left: list.scrollLeft + delta,
    behavior: "smooth",
  });
};

// TODO: do same as in col 4, do not fiddle with index
const activeTabRef = computed(() => unref(tabRefs.value[activeTabIndex.value]));

watch(activeTabRef, async (el) => {
  await nextTick();
  if (!el) {
    return;
  }
  if (isOverflowing(el, "left") || isOverflowing(el, "right")) {
    scrollToTab(el);
  }
});

const { setDropIndex, dragStart, dragOver, state, elementsMoved, dragging } =
  useDrag(tabList);

const elementsWithTransform = computed(() => {
  if (state.from === null || elementsMoved.value === 0) {
    return [];
  }

  const tabIndices = tabs.value.map((_, i) => i);

  if (elementsMoved.value > 0) {
    return tabIndices.slice(
      state.from + 1,
      state.from + 1 + elementsMoved.value
    );
  } else if (elementsMoved.value < 0) {
    return tabIndices.slice(state.from + elementsMoved.value, state.from);
  }
  // should never be reached but needed to make vue-tsc happy
  return [];
});

const onDrop = (e: DragEvent) => {
  setDropIndex(e);
  moveTab(state.from as number, state.to as number);
};

const preventScrollEvent = (e: KeyboardEvent) => {
  if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
    e.preventDefault();
  }
};
</script>
